import React from "react";
import { Field, ErrorMessage } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import Select from "react-select";
import { currencyOptions } from "../../utils/currencyOptions";
import { formatNumberWithCommas } from "../../utils/formatNumber";


export const paymentScheduleOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'halfyearly', label: 'Half-Yearly' },
  { value: 'yearly', label: 'Yearly' }
];

export const bnplInitialValues = {
  name: "",
  merchantName: "",
  description: "",
  totalAmount: "",
  remainingBalance: "",
  paymentSchedule: "",
  startDate: null,
  nextPaymentDate: null,
  nextPaymentAmount: "",
  remainingPayments: "",
  interestFees: "",
  currency: "",
};

export const bnplValidationSchema = Yup.object().shape({
  name: Yup.string()
      .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      )
      .required("Name is required"),
  merchantName: Yup.string()
  .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
        "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
      )
      .required("Merchant Name is required"),
  description: Yup.string()
    .required("Purchase description is required")
    .trim()
    .min(3, "Minimum 3 characters required")
    .max(250, "Maximum 250 characters allowed")
    .matches(
      /^[A-Za-z0-9\s\-_.&,()]+$/,
      "Only letters, numbers, spaces, and - _ . & , ( ) are allowed"
    ),
  totalAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Total amount is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  remainingBalance: Yup.number()
    .typeError("Must be a positive number")
    .required("Remaining balance is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  paymentSchedule: Yup.string()
    .required("Payment schedule is required")
    .oneOf(
      ['weekly', 'fortnightly', 'monthly', 'quarterly', 'halfyearly', 'yearly'],
      "Please select a valid payment schedule"
    ),
  startDate: Yup.date(),
  nextPaymentDate: Yup.date().required("Next payment date is required"),
  nextPaymentAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Next payment amount is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  remainingPayments: Yup.number().typeError("Must be a number").required("Number of remaining payments is required").min(1, "Value must be 1 or greater"),
  interestFees: Yup.number()
    .typeError("Must be a positive number")
    .required("Interest/fees is required")
    .positive("Must be a positive number > 0")
    .min(0, "Interest cannot be negative")
    .max(50, "Interest/Fees cannot exceed 50%"),
  currency: Yup.string().required("Currency is required"),
});

const BNPLForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  return (
    <>
          {/* Name */}
      <div className="col-md-12 mb-4">
        <label className="form-label">
          Liability Name<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="name"
          placeholder="e.g., Buy Now Pay Later"
          className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          
        />
        <ErrorMessage name="name" component="div" className="invalid-feedback" />
      </div>
    <div className="row">

      {/* merchantName/Service Provider */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          merchantName/Service Provider<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="merchantName"
          placeholder="e.g., Affirm, Afterpay"
          className={`form-control ${errors.merchantName && touched.merchantName ? "is-invalid" : ""}`}
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="merchantName" component="div" className="invalid-feedback" />
      </div>

      {/* Purchase Description */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Purchase Description<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="description"
          placeholder="e.g., Laptop, Smartphone"
         readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.description && touched.description ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="description" component="div" className="invalid-feedback" />
      </div>

      {/* Total Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Total Amount<span className="text-danger">*</span>
        </label>
      <div className="d-flex gap-2">
         <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
          />
        <Field
          type="text"
          name="totalAmount"
          className={`form-control ${errors.totalAmount && touched.totalAmount ? "is-invalid" : ""}`}
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          placeholder="e.g., 15,000"
          value={values.totalAmount ? formatNumberWithCommas(values.totalAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("totalAmount", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("totalAmount", unformatted)
            }
          }}
        />
        </div>
         <ErrorMessage name="currency" component="div" className="invalid-feedback" />
        <ErrorMessage name="totalAmount" component="div" className="text-danger small" />
      </div>

      {/* Remaining Balance */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Remaining Balance<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="remainingBalance"
          className={`form-control ${errors.remainingBalance && touched.remainingBalance ? "is-invalid" : ""}`}
          placeholder="e.g., 12,000"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.remainingBalance ? formatNumberWithCommas(values.remainingBalance) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("remainingBalance", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("remainingBalance", unformatted)
            }
          }}
        />
        <ErrorMessage name="remainingBalance" component="div" className="text-danger small" />
      </div>


<div className="col-md-6 mb-4">
        <label className="form-label">
          Start Date<span className="text-danger">*</span>
        </label>
        <DatePicker
          selected={values.startDate ? new Date(values.startDate) : null}
          onChange={(date) => setFieldValue("startDate", date)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.startDate && touched.startDate ? "is-invalid" : ""}`}
          placeholderText="Select a date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
             maxDate={new Date()}
          
        />
        <ErrorMessage name="startDate" component="div" className="text-danger small" />
      </div>
      {/* Next Payment Date (react-datepicker) */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Next Payment Date<span className="text-danger">*</span>
        </label>
        <DatePicker
          selected={values.nextPaymentDate ? new Date(values.nextPaymentDate) : null}
          onChange={(date) => setFieldValue("nextPaymentDate", date)}
          dateFormat="yyyy-MM-dd"
          className={`form-control ${errors.nextPaymentDate && touched.nextPaymentDate ? "is-invalid" : ""}`}
          placeholderText="Select a date"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
             minDate={new Date()}
        />
        <ErrorMessage name="nextPaymentDate" component="div" className="text-danger small" />
      </div>

      {/* Start Date (react-datepicker) */}
            {/* Payment Schedule */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Payment Schedule<span className="text-danger">*</span>
        </label>
        <Select
          options={paymentScheduleOptions}
          value={paymentScheduleOptions.find(option => option.value === values.paymentSchedule)}
          onChange={option => setFieldValue("paymentSchedule", option ? option.value : "")}
          className={`react-select-container ${errors.paymentSchedule && touched.paymentSchedule ? "is-invalid" : ""}`}
          classNamePrefix="react-select"
          isDisabled={localReadOnly}
          placeholder="Select payment schedule"
        />
        <ErrorMessage name="paymentSchedule" component="div" className="invalid-feedback" />
      </div>

      {/* Next Payment Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Next Payment Amount<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="nextPaymentAmount"
          className={`form-control ${errors.nextPaymentAmount && touched.nextPaymentAmount ? "is-invalid" : ""}`}
          placeholder="e.g., 3,000"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.nextPaymentAmount ? formatNumberWithCommas(values.nextPaymentAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("nextPaymentAmount", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("nextPaymentAmount", unformatted)
            }
          }}
        />
        <ErrorMessage name="nextPaymentAmount" component="div" className="text-danger small" />
      </div>

      {/* Number of Remaining Payments */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Number of Remaining Payments<span className="text-danger">*</span>
        </label>
        <Field
          type="number"
          name="remainingPayments"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.remainingPayments && touched.remainingPayments ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="remainingPayments" component="div" className="invalid-feedback" />
      </div>

      {/* Interest/Fees */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Interest/Fees<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="interestFees"
          className={`form-control ${errors.interestFees && touched.interestFees ? "is-invalid" : ""}`}
          placeholder="e.g., 500"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.interestFees ? formatNumberWithCommas(values.interestFees) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("interestFees", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("interestFees", unformatted)
            }
          }}
        />
        <ErrorMessage name="interestFees" component="div" className="invalid-feedback" />
      </div>
    </div>
</>
  );
};

export default BNPLForm;
